<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Database,
  ExternalLink,
  Filter,
  Network,
  Radar,
  RefreshCcw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from 'lucide-vue-next'

import CopyableAddress from '@/components/CopyableAddress.vue'
import type {
  Capabilities,
  OnchainCryptoGraph,
  OnchainEpisodeEvent,
  OnchainHolderSnapshot,
  OnchainRuntimeStatus,
  OnchainRadarState,
  OnchainRadarView,
  OnchainRiskCheck,
  OnchainRiskFlag,
  OnchainRiskStatus,
  OnchainScamRiskLevel,
  OnchainScoreBreakdown,
  OnchainSmartFlowResponse,
  OnchainWalletCluster,
} from '@/types/monitor'
import { compactAddress, formatLocalDateTime, truncate } from '@/utils/format'

const props = defineProps<{
  items: OnchainRadarView[]
  loading: boolean
  error: string | null
  runtimeStatus: OnchainRuntimeStatus | null
  runtimeStatusLoading: boolean
  runtimeStatusError: string | null
  holderHistory: OnchainHolderSnapshot[]
  holderHistoryLoading: boolean
  holderHistoryError: string | null
  holderHistoryWindowSeconds: number
  smartFlow: OnchainSmartFlowResponse | null
  smartFlowLoading: boolean
  smartFlowError: string | null
  smartFlowWindowSeconds: number
  cryptoGraph: OnchainCryptoGraph | null
  cryptoGraphLoading: boolean
  cryptoGraphError: string | null
  cryptoGraphWindowSeconds: number
  stateFilter: OnchainRadarState | null
  riskFilter: OnchainRiskStatus | null
  sourceFilter: string
  tokenQuery: string
  hasMore: boolean
  loadedAt: string | null
  selected: OnchainRadarView | null
  clusters: OnchainWalletCluster[]
  clustersLoading: boolean
  clustersError: string | null
  timeline: OnchainEpisodeEvent[]
  timelineLoading: boolean
  timelineHasMore: boolean
  capabilities: Capabilities
}>()

const emit = defineEmits<{
  refresh: []
  filter: [state: OnchainRadarState | null, risk: OnchainRiskStatus | null, source: string, tokenQuery: string]
  'load-more': []
  select: [assetId: string]
  'close-detail': []
  'holder-window': [windowSeconds: number]
  'smart-flow-window': [windowSeconds: number]
  'crypto-graph-window': [windowSeconds: number]
  'load-more-events': []
}>()

const { t, locale } = useI18n()
const stateDraft = ref<OnchainRadarState | ''>(props.stateFilter ?? '')
const riskDraft = ref<OnchainRiskStatus | ''>(props.riskFilter ?? '')
const sourceDraft = ref(props.sourceFilter)
const tokenDraft = ref(props.tokenQuery)

watch(
  () => [props.stateFilter, props.riskFilter, props.sourceFilter, props.tokenQuery] as const,
  ([state, risk, source, token]) => {
    stateDraft.value = state ?? ''
    riskDraft.value = risk ?? ''
    sourceDraft.value = source
    tokenDraft.value = token
  },
)

const stateOptions: OnchainRadarState[] = [
  'discovered',
  'candidate',
  'watch',
  'hot',
  'urgent',
  'cooled',
  'invalidated',
]
const riskOptions: OnchainRiskStatus[] = ['clean', 'elevated', 'hard_risk']
const holderWindowOptions = [
  { seconds: 3_600, label: '1H' },
  { seconds: 21_600, label: '6H' },
  { seconds: 86_400, label: '24H' },
  { seconds: 604_800, label: '7D' },
] as const
const smartFlowWindowOptions = holderWindowOptions
const cryptoGraphWindowOptions = holderWindowOptions
const sourceSuggestions = computed(() =>
  Array.from(new Set(props.items.map((item) => item.discovery_source))).sort(),
)
const urgentCount = computed(() => props.items.filter((item) => item.episode.state === 'urgent').length)
const hotCount = computed(() => props.items.filter((item) => item.episode.state === 'hot').length)
const hardRiskCount = computed(() => props.items.filter((item) => item.episode.risk_status === 'hard_risk').length)
const holderHistoryChronological = computed(() =>
  [...props.holderHistory].sort(
    (left, right) => Date.parse(left.observed_at) - Date.parse(right.observed_at),
  ),
)
const latestHolderSnapshot = computed(
  () => holderHistoryChronological.value[holderHistoryChronological.value.length - 1] ?? null,
)
const holderHistoryCoverageCount = computed(
  () => props.holderHistory.filter((snapshot) => snapshot.coverage_complete).length,
)
const smartFlowWallets = computed(() => props.smartFlow?.series.wallets.filter((wallet) => wallet.smart) ?? [])
const smartFlowNetUsd = computed(
  () =>
    (props.smartFlow?.series.organic_smart_buy_volume_usd ?? 0) -
    (props.smartFlow?.series.organic_smart_sell_volume_usd ?? 0),
)
const cryptoGraphVerifiedOutcomeCount = computed(
  () =>
    props.cryptoGraph?.telegram_evidence.reduce(
      (count, evidence) => count + evidence.alpha_outcomes.length,
      0,
    ) ?? 0,
)
const coveredRiskCount = computed(
  () => props.items.filter((item) => (item.risk_snapshot?.coverage_ratio ?? 0) >= 0.8).length,
)
const activeFilterCount = computed(
  () =>
    Number(Boolean(stateDraft.value)) +
    Number(Boolean(riskDraft.value)) +
    Number(Boolean(sourceDraft.value.trim())) +
    Number(Boolean(tokenDraft.value.trim())),
)

function applyFilters(): void {
  emit(
    'filter',
    stateDraft.value || null,
    riskDraft.value || null,
    sourceDraft.value,
    tokenDraft.value,
  )
}

function resetFilters(): void {
  stateDraft.value = ''
  riskDraft.value = ''
  sourceDraft.value = ''
  tokenDraft.value = ''
  applyFilters()
}

function onTokenKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') applyFilters()
}

function assetLabel(item: OnchainRadarView): string {
  return item.symbol || item.name || compactAddress(item.contract_address)
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat(locale.value === 'zh-CN' ? 'zh-CN' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    notation: Math.abs(value) >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) < 1 ? 4 : 1,
  }).format(value)
}

function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

function formatRatio(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return value.toFixed(1)
}

function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat(locale.value === 'zh-CN' ? 'zh-CN' : 'en-US', {
    maximumFractionDigits: 0,
  }).format(value)
}

function seriesPoints(values: number[], minValue: number, maxValue: number): string {
  if (values.length === 0) return ''
  const width = 320
  const height = 70
  const padX = 4
  const padY = 5
  const usableWidth = width - padX * 2
  const usableHeight = height - padY * 2
  const span = Math.max(maxValue - minValue, Number.EPSILON)
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : padX + (index / (values.length - 1)) * usableWidth
      const normalized = Math.min(1, Math.max(0, (value - minValue) / span))
      const y = padY + (1 - normalized) * usableHeight
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
}

function holderCountSeriesPoints(field: 'holder_count' | 'organic_holder_count'): string {
  const rows = holderHistoryChronological.value
  const maxValue = Math.max(
    1,
    ...rows.flatMap((row) => [row.holder_count, row.organic_holder_count]),
  )
  return seriesPoints(rows.map((row) => row[field]), 0, maxValue)
}

function holderRatioSeriesPoints(field: 'recognized_holder_ratio' | 'smart_holder_ratio'): string {
  const rows = holderHistoryChronological.value
  const maxValue = Math.min(
    1,
    Math.max(
      0.25,
      ...rows.flatMap((row) => [row.recognized_holder_ratio, row.smart_holder_ratio]),
    ),
  )
  return seriesPoints(rows.map((row) => row[field]), 0, maxValue)
}

function top10ConcentrationSeriesPoints(): string {
  const rows = holderHistoryChronological.value
  const values = rows
    .map((row) => row.top10_concentration)
    .filter((value): value is number => value !== null && Number.isFinite(value))
  return seriesPoints(values, 0, 1)
}

function smartFlowNetValues(): number[] {
  return (props.smartFlow?.series.buckets ?? []).map(
    (bucket) => bucket.organic_smart_buy_volume_usd - bucket.organic_smart_sell_volume_usd,
  )
}

function smartFlowNetSeriesPoints(): string {
  const values = smartFlowNetValues()
  if (values.length === 0) return ''
  const minValue = Math.min(0, ...values)
  const maxValue = Math.max(0, ...values)
  return seriesPoints(values, minValue, maxValue)
}

function smartFlowVolumeSeriesPoints(
  field: 'organic_buy_volume_usd' | 'organic_sell_volume_usd',
): string {
  const buckets = props.smartFlow?.series.buckets ?? []
  const maxValue = Math.max(
    1,
    ...buckets.flatMap((bucket) => [
      bucket.organic_buy_volume_usd,
      bucket.organic_sell_volume_usd,
    ]),
  )
  return seriesPoints(
    buckets.map((bucket) => bucket[field]),
    0,
    maxValue,
  )
}

function smartFlowZeroY(): number {
  const values = smartFlowNetValues()
  if (values.length === 0) return 35
  const minValue = Math.min(0, ...values)
  const maxValue = Math.max(0, ...values)
  const span = Math.max(maxValue - minValue, Number.EPSILON)
  return 5 + (1 - (0 - minValue) / span) * 60
}

function smartPerformanceFor(address: string) {
  const normalized = address.toLowerCase()
  return (
    props.smartFlow?.wallet_performance.find((entry) => entry.address.toLowerCase() === normalized)
      ?.performance ?? null
  )
}

function walletWinRate(wins: number | undefined, trades: number | undefined): string {
  if (!trades) return '—'
  return formatRatio((wins ?? 0) / trades)
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3_600) return `${(seconds / 60).toFixed(seconds < 600 ? 1 : 0)}m`
  if (seconds < 86_400) return `${(seconds / 3_600).toFixed(seconds < 36_000 ? 1 : 0)}h`
  return `${(seconds / 86_400).toFixed(1)}d`
}

function graphIdentityLinksForWallet(walletId: string) {
  return props.cryptoGraph?.identities.filter((identity) => identity.wallet_id === walletId) ?? []
}

function graphTelegramPerson(identityKey: string) {
  const telegramId = Number(identityKey)
  if (!Number.isSafeInteger(telegramId)) return null
  return props.cryptoGraph?.persons.find((person) => person.telegram_id === telegramId) ?? null
}

function graphClusterForWallet(clusterIds: string[]) {
  if (!props.cryptoGraph || clusterIds.length === 0) return null
  return props.cryptoGraph.clusters.find((cluster) => clusterIds.includes(cluster.id)) ?? null
}

function graphPersonLabel(identityKey: string): string {
  const person = graphTelegramPerson(identityKey)
  if (!person) return `Telegram ${identityKey}`
  return person.current_name || (person.current_username ? `@${person.current_username}` : `Telegram ${identityKey}`)
}

function graphPersonSecondary(identityKey: string): string {
  const person = graphTelegramPerson(identityKey)
  if (!person) return identityKey
  const username = person.current_username ? `@${person.current_username}` : `ID ${person.telegram_id}`
  const aliases = person.aliases.slice(0, 2).map((alias) => alias.alias_value)
  return aliases.length ? `${username} · ${aliases.join(' / ')}` : username
}

function graphOutcomeLabel(horizonSeconds: number): string {
  if (horizonSeconds < 3_600) return `${Math.round(horizonSeconds / 60)}m`
  if (horizonSeconds < 86_400) return `${Math.round(horizonSeconds / 3_600)}h`
  return `${Math.round(horizonSeconds / 86_400)}d`
}

function scoreParts(score: OnchainScoreBreakdown | undefined): Array<{ key: keyof OnchainScoreBreakdown; value: number }> {
  if (!score) return []
  return [
    { key: 'discovery_quality', value: score.discovery_quality },
    { key: 'holder_momentum', value: score.holder_momentum },
    { key: 'smart_money_quality', value: score.smart_money_quality },
    { key: 'capital_flow', value: score.capital_flow },
    { key: 'liquidity_quality', value: score.liquidity_quality },
    { key: 'independent_sources', value: score.independent_sources },
    { key: 'social_confirmation', value: score.social_confirmation },
    { key: 'risk_penalty', value: score.risk_penalty },
  ]
}

function stateClass(state: OnchainRadarState): string {
  return `radar-state radar-state--${state}`
}

function riskClass(risk: OnchainRiskStatus): string {
  return `radar-risk radar-risk--${risk}`
}

function scamClass(level: OnchainScamRiskLevel): string {
  return `scam-risk scam-risk--${level}`
}

function providerHealthLabel(): string {
  if (props.runtimeStatusLoading) return t('radar.providerChecking')
  if (props.runtimeStatusError) return t('radar.providerError')
  const status = props.runtimeStatus
  if (!status?.provider_available) return t('radar.providerUnavailable')
  if (status.health?.circuit_open) return t('radar.providerCircuitOpen')
  if (status.health?.healthy) return t('radar.providerHealthy')
  return t('radar.providerDegraded')
}

function providerHealthTitle(): string {
  return (
    props.runtimeStatus?.health?.last_error ||
    props.runtimeStatusError ||
    t('radar.providerNoError')
  )
}

function providerHealthClass(): string {
  if (props.runtimeStatus?.health?.healthy && !props.runtimeStatus.health.circuit_open) return 'healthy'
  if (props.runtimeStatus?.degraded || props.runtimeStatusError) return 'degraded'
  return ''
}

function scamFactorLabel(key: string): string {
  return t(`radar.scamFactors.${key}`)
}

function contractMatchLabel(value: boolean | null | undefined): string {
  if (value === true) return t('radar.contractMatchExact')
  if (value === false) return t('radar.contractMatchConflict')
  return t('radar.contractMatchUnknown')
}

function riskFlagLabel(flag: OnchainRiskFlag): string {
  return t(`radar.riskFlags.${flag}`)
}

function riskCheckLabel(check: OnchainRiskCheck): string {
  return t(`radar.checkStatus.${check.status}`)
}

function evidenceReason(event: OnchainEpisodeEvent): string | null {
  if (!event.evidence || typeof event.evidence !== 'object' || Array.isArray(event.evidence)) return null
  const reason = (event.evidence as Record<string, unknown>).reason
  return typeof reason === 'string' ? reason : null
}

function evidencePreview(value: unknown): string {
  try {
    return truncate(JSON.stringify(value), 260)
  } catch {
    return '—'
  }
}

type WalletClusterEdge = {
  wallets: [string, string]
  confidence: number
  features: unknown
}

function clusterEvidence(cluster: OnchainWalletCluster): Record<string, unknown> {
  if (!cluster.evidence || typeof cluster.evidence !== 'object' || Array.isArray(cluster.evidence)) return {}
  return cluster.evidence as Record<string, unknown>
}

function clusterMetric(cluster: OnchainWalletCluster, key: string): number | null {
  const value = clusterEvidence(cluster)[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function clusterEdges(cluster: OnchainWalletCluster): WalletClusterEdge[] {
  const value = clusterEvidence(cluster).edges
  if (!Array.isArray(value)) return []
  return value.flatMap((edge) => {
    if (!edge || typeof edge !== 'object' || Array.isArray(edge)) return []
    const record = edge as Record<string, unknown>
    if (!Array.isArray(record.wallets) || record.wallets.length !== 2) return []
    const [left, right] = record.wallets
    if (typeof left !== 'string' || typeof right !== 'string') return []
    if (typeof record.confidence !== 'number' || !Number.isFinite(record.confidence)) return []
    return [{ wallets: [left, right], confidence: record.confidence, features: record.features }]
  })
}

function clusterLimitations(cluster: OnchainWalletCluster): string[] {
  const value = clusterEvidence(cluster).limitations
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}
</script>

<template>
  <section class="radar-workspace">
    <header class="radar-hero">
      <div>
        <div class="radar-kicker"><Radar :size="14" /> {{ t('radar.kicker') }}</div>
        <h1>{{ t('radar.title') }}</h1>
        <p>{{ t('radar.description') }}</p>
      </div>
      <div class="radar-health">
        <span :class="['health-chip', { healthy: capabilities.onchain_enabled }]">
          <Database :size="13" />
          {{ t('radar.ingest') }} · {{ capabilities.onchain_enabled ? t('common.live') : t('common.disabled') }}
        </span>
        <span :class="['health-chip', { healthy: capabilities.onchain_radar_enabled }]">
          <Activity :size="13" />
          {{ t('radar.worker') }} · {{ capabilities.onchain_radar_enabled ? t('common.live') : t('common.disabled') }}
        </span>
        <span :class="['health-chip', providerHealthClass()]" :title="providerHealthTitle()">
          <Network :size="13" />
          {{ runtimeStatus?.provider || t('radar.providerUnknown') }} · {{ providerHealthLabel() }}
        </span>
        <span class="health-chip"><Network :size="13" />{{ capabilities.onchain_chain_id }}</span>
        <button class="radar-icon-button" :disabled="loading" :title="t('radar.refresh')" @click="emit('refresh')">
          <RefreshCcw :size="15" :class="{ spinning: loading }" />
        </button>
      </div>
    </header>

    <div v-if="runtimeStatus" class="provider-runtime-strip">
      <div><span>{{ t('radar.providerBlock') }}</span><strong>{{ formatInteger(runtimeStatus.health?.latest_block) }}</strong></div>
      <div><span>{{ t('radar.providerFailures') }}</span><strong>{{ runtimeStatus.health?.consecutive_failures ?? '—' }}</strong></div>
      <div><span>{{ t('radar.providerLastSuccess') }}</span><strong>{{ runtimeStatus.health?.last_success_at ? formatLocalDateTime(runtimeStatus.health.last_success_at) : '—' }}</strong></div>
      <div><span>{{ t('radar.providerUpdated') }}</span><strong>{{ runtimeStatus.health?.updated_at ? formatLocalDateTime(runtimeStatus.health.updated_at) : '—' }}</strong></div>
      <div v-if="runtimeStatus.health?.last_error" class="provider-runtime-error" :title="runtimeStatus.health.last_error">
        <span>{{ t('radar.providerLastError') }}</span><strong>{{ truncate(runtimeStatus.health.last_error, 140) }}</strong>
      </div>
    </div>

    <div class="radar-summary-grid">
      <article class="radar-summary-card">
        <span>{{ t('radar.loadedAssets') }}</span><strong>{{ items.length }}</strong>
        <small>{{ loadedAt ? formatLocalDateTime(loadedAt) : t('radar.notLoaded') }}</small>
      </article>
      <article class="radar-summary-card">
        <span>{{ t('radar.hotUrgent') }}</span><strong>{{ hotCount + urgentCount }}</strong>
        <small>{{ t('radar.hotUrgentDetail', { hot: hotCount, urgent: urgentCount }) }}</small>
      </article>
      <article class="radar-summary-card">
        <span>{{ t('radar.hardRisk') }}</span><strong>{{ hardRiskCount }}</strong>
        <small>{{ t('radar.hardRiskHint') }}</small>
      </article>
      <article class="radar-summary-card">
        <span>{{ t('radar.riskCoverage') }}</span><strong>{{ coveredRiskCount }}/{{ items.length }}</strong>
        <small>{{ t('radar.riskCoverageHint') }}</small>
      </article>
    </div>

    <div class="radar-filter-bar">
      <div class="filter-field filter-field--search">
        <label>{{ t('radar.tokenFilter') }}</label>
        <div class="input-shell"><Search :size="14" /><input v-model="tokenDraft" :placeholder="t('radar.tokenPlaceholder')" maxlength="128" @keydown="onTokenKeydown" /></div>
      </div>
      <div class="filter-field">
        <label>{{ t('radar.chainFilter') }}</label>
        <input :value="capabilities.onchain_chain_id" disabled />
      </div>
      <div class="filter-field">
        <label>{{ t('radar.sourceFilter') }}</label>
        <input v-model="sourceDraft" list="radar-source-options" :placeholder="t('radar.sourcePlaceholder')" maxlength="128" />
        <datalist id="radar-source-options"><option v-for="source in sourceSuggestions" :key="source" :value="source" /></datalist>
      </div>
      <div class="filter-field">
        <label>{{ t('radar.stateFilter') }}</label>
        <select v-model="stateDraft">
          <option value="">{{ t('common.all') }}</option>
          <option v-for="state in stateOptions" :key="state" :value="state">{{ t(`radar.states.${state}`) }}</option>
        </select>
      </div>
      <div class="filter-field">
        <label>{{ t('radar.riskFilter') }}</label>
        <select v-model="riskDraft">
          <option value="">{{ t('common.all') }}</option>
          <option v-for="risk in riskOptions" :key="risk" :value="risk">{{ t(`radar.risks.${risk}`) }}</option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="radar-button radar-button--primary" @click="applyFilters"><Filter :size="14" />{{ t('radar.applyFilter') }}<span v-if="activeFilterCount">{{ activeFilterCount }}</span></button>
        <button class="radar-button" :disabled="activeFilterCount === 0" @click="resetFilters">{{ t('radar.resetFilter') }}</button>
      </div>
    </div>

    <div v-if="error" class="radar-error"><AlertTriangle :size="16" />{{ error }}</div>

    <div class="radar-layout">
      <section class="radar-list-panel">
        <div class="panel-heading">
          <div><strong>{{ t('radar.assetRadar') }}</strong><small>{{ t('radar.assetRadarHint') }}</small></div>
          <span>{{ items.length }} {{ t('radar.rows') }}</span>
        </div>

        <div class="radar-table-wrap">
          <table class="radar-table">
            <thead>
              <tr>
                <th>{{ t('radar.token') }}</th>
                <th>{{ t('radar.state') }}</th>
                <th class="numeric">{{ t('radar.score') }}</th>
                <th class="numeric">{{ t('radar.liquidity') }}</th>
                <th class="numeric">{{ t('radar.holder5m') }}</th>
                <th class="numeric">{{ t('radar.smartHolder') }}</th>
                <th class="numeric">{{ t('radar.netInflow5m') }}</th>
                <th class="numeric">{{ t('radar.independent') }}</th>
                <th>{{ t('radar.scamRisk') }}</th>
                <th>{{ t('radar.risk') }}</th>
                <th>{{ t('radar.evaluated') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in items"
                :key="item.asset_id"
                :class="{ selected: selected?.asset_id === item.asset_id }"
                tabindex="0"
                @click="emit('select', item.asset_id)"
                @keydown.enter="emit('select', item.asset_id)"
              >
                <td class="token-cell">
                  <strong>{{ item.symbol || item.name || '—' }}</strong>
                  <span v-if="item.symbol && item.name">{{ item.name }}</span>
                  <CopyableAddress :value="item.contract_address" class="token-address" />
                  <small>{{ item.discovery_source }}</small>
                </td>
                <td><span :class="stateClass(item.episode.state)">{{ t(`radar.states.${item.episode.state}`) }}</span></td>
                <td class="numeric score-cell">{{ formatScore(item.episode.score) }}</td>
                <td class="numeric">{{ formatMoney(item.latest_liquidity_usd) }}</td>
                <td class="numeric metric-delta" :class="{ positive: (item.radar_input?.holder.holder_growth_5m_percent ?? 0) > 0 }">
                  {{ formatPercent(item.radar_input?.holder.holder_growth_5m_percent) }}
                </td>
                <td class="numeric">{{ formatRatio(item.radar_input?.holder.smart_holder_ratio) }}</td>
                <td class="numeric metric-delta" :class="{ positive: (item.radar_input?.flow.organic_net_inflow_5m_usd ?? 0) > 0, negative: (item.radar_input?.flow.organic_net_inflow_5m_usd ?? 0) < 0 }">
                  {{ formatMoney(item.radar_input?.flow.organic_net_inflow_5m_usd) }}
                </td>
                <td class="numeric">{{ item.radar_input?.holder.independent_smart_clusters ?? '—' }} / {{ item.radar_input?.independent_source_count ?? '—' }}</td>
                <td>
                  <span v-if="item.evaluation?.scam_guard" :class="scamClass(item.evaluation.scam_guard.level)">
                    {{ t(`radar.scamLevels.${item.evaluation.scam_guard.level}`) }} · {{ formatScore(item.evaluation.scam_guard.score) }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td><span :class="riskClass(item.episode.risk_status)">{{ t(`radar.risks.${item.episode.risk_status}`) }}</span></td>
                <td class="time-cell" :title="formatLocalDateTime(item.evaluated_at)">{{ formatLocalDateTime(item.evaluated_at) }}</td>
              </tr>
              <tr v-if="!loading && items.length === 0"><td colspan="11" class="empty-cell">{{ t('radar.empty') }}</td></tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer">
          <span v-if="loading"><RefreshCcw :size="13" class="spinning" />{{ t('radar.loading') }}</span>
          <span v-else>{{ t('radar.pageHint') }}</span>
          <button v-if="hasMore" class="radar-button" :disabled="loading" @click="emit('load-more')"><ChevronDown :size="14" />{{ t('radar.loadMore') }}</button>
        </div>
      </section>

      <aside v-if="selected" class="radar-detail-panel">
        <div class="detail-header">
          <div>
            <div class="detail-title-line">
              <h2>{{ assetLabel(selected) }}</h2>
              <span :class="stateClass(selected.episode.state)">{{ t(`radar.states.${selected.episode.state}`) }}</span>
              <span v-if="selected.evaluation?.scam_guard" :class="scamClass(selected.evaluation.scam_guard.level)">{{ t(`radar.scamLevels.${selected.evaluation.scam_guard.level}`) }}</span>
              <span :class="riskClass(selected.episode.risk_status)">{{ t(`radar.risks.${selected.episode.risk_status}`) }}</span>
            </div>
            <p>{{ selected.name || selected.discovery_source }}</p>
            <CopyableAddress :value="selected.contract_address" />
          </div>
          <button class="radar-icon-button" :title="t('common.close')" @click="emit('close-detail')"><X :size="16" /></button>
        </div>

        <div class="detail-meta-grid">
          <div><span>{{ t('radar.score') }}</span><strong>{{ formatScore(selected.episode.score) }}</strong></div>
          <div><span>{{ t('radar.marketCap') }}</span><strong>{{ formatMoney(selected.latest_market_cap_usd) }}</strong></div>
          <div><span>{{ t('radar.liquidity') }}</span><strong>{{ formatMoney(selected.latest_liquidity_usd) }}</strong></div>
          <div><span>{{ t('radar.price') }}</span><strong>{{ formatMoney(selected.latest_price_usd) }}</strong></div>
          <div><span>{{ t('radar.firstSeen') }}</span><strong :title="formatLocalDateTime(selected.first_seen_at)">{{ formatLocalDateTime(selected.first_seen_at) }}</strong></div>
          <div><span>{{ t('radar.events') }}</span><strong>{{ selected.episode.event_count }}</strong></div>
        </div>

        <section class="detail-section">
          <div class="section-title"><Sparkles :size="15" /><strong>{{ t('radar.scoreBreakdown') }}</strong><span>{{ t('radar.explainableScore') }}</span></div>
          <div v-if="selected.evaluation" class="score-grid">
            <div v-for="part in scoreParts(selected.evaluation.score)" :key="part.key" class="score-part">
              <span>{{ t(`radar.scoreParts.${part.key}`) }}</span>
              <strong :class="{ penalty: part.key === 'risk_penalty' }">{{ part.key === 'risk_penalty' ? '−' : '' }}{{ formatScore(part.value) }}</strong>
            </div>
          </div>
          <div v-else class="inline-empty">{{ t('radar.legacyMissingEvaluation') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title"><Users :size="15" /><strong>{{ t('radar.holderVelocity') }}</strong><span>{{ t('radar.deterministicFacts') }}</span></div>
          <div v-if="selected.radar_input" class="metric-grid">
            <div><span>{{ t('radar.holders') }}</span><strong>{{ selected.radar_input.holder.holder_count.toLocaleString() }}</strong><small>{{ t('radar.organic') }} {{ selected.radar_input.holder.organic_holder_count.toLocaleString() }}</small></div>
            <div><span>{{ t('radar.growth1m') }}</span><strong>{{ formatPercent(selected.radar_input.holder.holder_growth_1m_percent) }}</strong></div>
            <div><span>{{ t('radar.growth5m') }}</span><strong>{{ formatPercent(selected.radar_input.holder.holder_growth_5m_percent) }}</strong></div>
            <div><span>{{ t('radar.growth15m') }}</span><strong>{{ formatPercent(selected.radar_input.holder.holder_growth_15m_percent) }}</strong></div>
            <div><span>{{ t('radar.dexBuyerRatio') }}</span><strong>{{ formatRatio(selected.radar_input.holder.dex_buyer_ratio) }}</strong></div>
            <div><span>{{ t('radar.distributionRatio') }}</span><strong>{{ formatRatio(selected.radar_input.holder.distribution_receiver_ratio) }}</strong></div>
            <div><span>{{ t('radar.recognized') }}</span><strong>{{ formatRatio(selected.radar_input.holder.recognized_holder_ratio) }}</strong><small>Δ5m {{ formatPercent(selected.radar_input.holder.recognized_penetration_delta_5m) }}</small></div>
            <div><span>{{ t('radar.smartHolder') }}</span><strong>{{ formatRatio(selected.radar_input.holder.smart_holder_ratio) }}</strong><small>Δ5m {{ formatPercent(selected.radar_input.holder.smart_penetration_delta_5m) }}</small></div>
            <div><span>{{ t('radar.smartClusters') }}</span><strong>{{ selected.radar_input.holder.independent_smart_clusters }}</strong><small>{{ selected.radar_input.holder.coverage_complete ? t('radar.coverageComplete') : t('radar.coverageIncomplete') }}</small></div>
          </div>
          <div v-else class="inline-empty">{{ t('radar.legacyMissingInput') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title holder-history-title">
            <Activity :size="15" />
            <strong>{{ t('radar.holderHistory') }}</strong>
            <span>{{ t('radar.holderHistoryHint') }}</span>
          </div>
          <div class="holder-history-toolbar">
            <div class="holder-history-windows">
              <button
                v-for="option in holderWindowOptions"
                :key="option.seconds"
                :class="['history-window-button', { active: holderHistoryWindowSeconds === option.seconds }]"
                :disabled="holderHistoryLoading"
                @click="emit('holder-window', option.seconds)"
              >
                {{ option.label }}
              </button>
            </div>
            <span v-if="holderHistory.length">
              {{ t('radar.holderHistoryPoints', { count: holderHistory.length }) }} ·
              {{ t('radar.holderHistoryCoverage') }} {{ holderHistoryCoverageCount }}/{{ holderHistory.length }}
            </span>
          </div>
          <div v-if="holderHistoryError" class="history-error"><AlertTriangle :size="13" />{{ holderHistoryError }}</div>
          <div v-if="holderHistoryLoading" class="inline-empty"><RefreshCcw :size="13" class="spinning" />{{ t('radar.holderHistoryLoading') }}</div>
          <template v-else-if="holderHistoryChronological.length">
            <div class="holder-history-grid">
              <article class="history-card">
                <div class="history-card-head">
                  <div><span>{{ t('radar.holderHistoryTotal') }}</span><strong>{{ formatInteger(latestHolderSnapshot?.holder_count) }}</strong></div>
                  <small>{{ t('radar.organic') }} {{ formatInteger(latestHolderSnapshot?.organic_holder_count) }}</small>
                </div>
                <svg class="history-chart" viewBox="0 0 320 70" preserveAspectRatio="none" aria-hidden="true">
                  <line class="history-grid-line" x1="4" y1="35" x2="316" y2="35" />
                  <polyline class="history-line history-line--holder" :points="holderCountSeriesPoints('holder_count')" />
                  <polyline class="history-line history-line--organic" :points="holderCountSeriesPoints('organic_holder_count')" />
                </svg>
                <div class="history-legend"><span class="legend-holder">{{ t('radar.holders') }}</span><span class="legend-organic">{{ t('radar.organic') }}</span></div>
              </article>
              <article class="history-card">
                <div class="history-card-head">
                  <div><span>{{ t('radar.holderHistoryPenetration') }}</span><strong>{{ formatRatio(latestHolderSnapshot?.recognized_holder_ratio) }}</strong></div>
                  <small>{{ t('radar.smartHolder') }} {{ formatRatio(latestHolderSnapshot?.smart_holder_ratio) }}</small>
                </div>
                <svg class="history-chart" viewBox="0 0 320 70" preserveAspectRatio="none" aria-hidden="true">
                  <line class="history-grid-line" x1="4" y1="35" x2="316" y2="35" />
                  <polyline class="history-line history-line--recognized" :points="holderRatioSeriesPoints('recognized_holder_ratio')" />
                  <polyline class="history-line history-line--smart" :points="holderRatioSeriesPoints('smart_holder_ratio')" />
                </svg>
                <div class="history-legend"><span class="legend-recognized">{{ t('radar.recognized') }}</span><span class="legend-smart">{{ t('radar.smartHolder') }}</span></div>
              </article>
              <article class="history-card">
                <div class="history-card-head">
                  <div><span>{{ t('radar.holderHistoryConcentration') }}</span><strong>{{ formatRatio(latestHolderSnapshot?.top10_concentration) }}</strong></div>
                  <small>Top25 {{ formatRatio(latestHolderSnapshot?.top25_concentration) }}</small>
                </div>
                <svg class="history-chart" viewBox="0 0 320 70" preserveAspectRatio="none" aria-hidden="true">
                  <line class="history-grid-line" x1="4" y1="35" x2="316" y2="35" />
                  <polyline class="history-line history-line--concentration" :points="top10ConcentrationSeriesPoints()" />
                </svg>
                <div class="history-legend"><span class="legend-concentration">Top10</span><span>{{ t('radar.lowerIsMoreDistributed') }}</span></div>
              </article>
            </div>
            <div class="history-footnote">
              <span>{{ t('radar.holderHistorySource') }} {{ latestHolderSnapshot?.source }}</span>
              <span v-if="holderHistoryChronological.length > 1">
                {{ t('radar.holderHistoryRange', {
                  start: formatLocalDateTime(holderHistoryChronological[0]!.observed_at),
                  end: formatLocalDateTime(holderHistoryChronological[holderHistoryChronological.length - 1]!.observed_at),
                }) }}
              </span>
              <span>{{ t('radar.holderHistoryPersisted') }}</span>
            </div>
          </template>
          <div v-else class="inline-empty">{{ t('radar.holderHistoryEmpty') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title holder-history-title">
            <CircleDollarSign :size="15" />
            <strong>{{ t('radar.smartFlowTitle') }}</strong>
            <span>{{ t('radar.smartFlowHint') }}</span>
          </div>
          <div class="holder-history-toolbar">
            <div class="holder-history-windows">
              <button
                v-for="option in smartFlowWindowOptions"
                :key="option.seconds"
                :class="['history-window-button', { active: smartFlowWindowSeconds === option.seconds }]"
                :disabled="smartFlowLoading"
                @click="emit('smart-flow-window', option.seconds)"
              >
                {{ option.label }}
              </button>
            </div>
            <span v-if="smartFlow">
              {{ t('radar.smartFlowBuckets', { count: smartFlow.series.buckets.length }) }} ·
              {{ t('radar.valuationCoverage') }} {{ formatRatio(smartFlow.valuation_coverage_ratio) }}
            </span>
          </div>
          <div v-if="smartFlowError" class="history-error"><AlertTriangle :size="13" />{{ smartFlowError }}</div>
          <div v-if="smartFlowLoading" class="inline-empty"><RefreshCcw :size="13" class="spinning" />{{ t('radar.smartFlowLoading') }}</div>
          <template v-else-if="smartFlow">
            <div class="smart-flow-summary">
              <div>
                <span>{{ t('radar.smartOrganicNet') }}</span>
                <strong :class="{ positive: smartFlowNetUsd > 0, negative: smartFlowNetUsd < 0 }">{{ formatMoney(smartFlowNetUsd) }}</strong>
                <small>{{ t('radar.smartWallets') }} {{ smartFlowWallets.length }}</small>
              </div>
              <div>
                <span>{{ t('radar.organicBuy') }}</span>
                <strong>{{ formatMoney(smartFlow.series.organic_buy_volume_usd) }}</strong>
                <small>{{ t('radar.raw') }} {{ formatMoney(smartFlow.series.buy_volume_usd) }}</small>
              </div>
              <div>
                <span>{{ t('radar.organicSell') }}</span>
                <strong>{{ formatMoney(smartFlow.series.organic_sell_volume_usd) }}</strong>
                <small>{{ t('radar.raw') }} {{ formatMoney(smartFlow.series.sell_volume_usd) }}</small>
              </div>
              <div>
                <span>{{ t('radar.flowFacts') }}</span>
                <strong>{{ formatInteger(smartFlow.series.flow_count) }}</strong>
                <small>{{ smartFlow.series.valued_flow_count }} {{ t('radar.valued') }} / {{ smartFlow.series.unvalued_flow_count }} {{ t('radar.unvalued') }}</small>
              </div>
            </div>

            <div class="smart-flow-charts">
              <article class="history-card">
                <div class="history-card-head">
                  <div><span>{{ t('radar.organicBuySellSeries') }}</span><strong>{{ formatMoney(smartFlow.series.organic_buy_volume_usd - smartFlow.series.organic_sell_volume_usd) }}</strong></div>
                  <small>{{ t('radar.bucket') }} {{ formatDuration(smartFlow.bucket_seconds) }}</small>
                </div>
                <svg class="history-chart" viewBox="0 0 320 70" preserveAspectRatio="none" aria-hidden="true">
                  <line class="history-grid-line" x1="4" y1="65" x2="316" y2="65" />
                  <polyline class="history-line smart-flow-line--buy" :points="smartFlowVolumeSeriesPoints('organic_buy_volume_usd')" />
                  <polyline class="history-line smart-flow-line--sell" :points="smartFlowVolumeSeriesPoints('organic_sell_volume_usd')" />
                </svg>
                <div class="history-legend"><span class="legend-organic-buy">{{ t('radar.buy') }}</span><span class="legend-organic-sell">{{ t('radar.sell') }}</span></div>
              </article>
              <article class="history-card">
                <div class="history-card-head">
                  <div><span>{{ t('radar.smartNetSeries') }}</span><strong>{{ formatMoney(smartFlowNetUsd) }}</strong></div>
                  <small>{{ t('radar.organicOnly') }}</small>
                </div>
                <svg class="history-chart" viewBox="0 0 320 70" preserveAspectRatio="none" aria-hidden="true">
                  <line class="history-grid-line" x1="4" :y1="smartFlowZeroY()" x2="316" :y2="smartFlowZeroY()" />
                  <polyline class="history-line smart-flow-line--net" :points="smartFlowNetSeriesPoints()" />
                </svg>
                <div class="history-legend"><span class="legend-smart-net">{{ t('radar.smartOrganicNet') }}</span><span>{{ t('radar.zeroBaseline') }}</span></div>
              </article>
            </div>

            <div class="smart-flow-subtitle">
              <strong>{{ t('radar.smartWalletBehavior') }}</strong>
              <span>{{ t('radar.smartWalletBehaviorHint') }}</span>
            </div>
            <div v-if="smartFlow.series.wallets.length" class="mini-table-wrap smart-flow-table-wrap">
              <table class="mini-table smart-flow-table">
                <thead>
                  <tr>
                    <th>{{ t('radar.wallet') }}</th>
                    <th>{{ t('radar.role') }}</th>
                    <th>{{ t('radar.buySell') }}</th>
                    <th>{{ t('radar.organicFlow') }}</th>
                    <th>{{ t('radar.pnl30d') }}</th>
                    <th>{{ t('radar.winRate30d') }}</th>
                    <th>{{ t('radar.quality') }}</th>
                    <th>{{ t('radar.holding') }}</th>
                    <th>{{ t('radar.cluster') }}</th>
                    <th>{{ t('radar.lastActivity') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="wallet in smartFlow.series.wallets.slice(0, 16)" :key="wallet.wallet_address">
                    <td><CopyableAddress :value="wallet.wallet_address" /></td>
                    <td><span :class="['smart-wallet-badge', { active: wallet.smart }]">{{ wallet.smart ? t('radar.smart') : t('radar.observed') }}</span></td>
                    <td>{{ wallet.buy_count }} / {{ wallet.sell_count }}</td>
                    <td>{{ formatMoney(wallet.organic_buy_volume_usd - wallet.organic_sell_volume_usd) }}</td>
                    <td>{{ formatMoney(smartPerformanceFor(wallet.wallet_address)?.realized_pnl_30d_usd) }}</td>
                    <td>{{ walletWinRate(smartPerformanceFor(wallet.wallet_address)?.wins_30d, smartPerformanceFor(wallet.wallet_address)?.trades_30d) }}</td>
                    <td>
                      {{ formatScore(smartPerformanceFor(wallet.wallet_address)?.quality_score) }}
                      <small v-if="smartPerformanceFor(wallet.wallet_address)"> · {{ formatRatio(smartPerformanceFor(wallet.wallet_address)?.confidence) }}</small>
                    </td>
                    <td>{{ formatDuration(smartPerformanceFor(wallet.wallet_address)?.median_holding_seconds) }}</td>
                    <td>{{ wallet.cluster_ids.length || '—' }}</td>
                    <td>{{ formatLocalDateTime(wallet.last_activity_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="inline-empty">{{ t('radar.smartWalletEmpty') }}</div>

            <div class="smart-flow-subtitle smart-flow-subtitle--flows">
              <strong>{{ t('radar.recentCanonicalFlows') }}</strong>
              <span>{{ t('radar.recentCanonicalFlowsHint') }}</span>
            </div>
            <div v-if="smartFlow.recent_flows.length" class="mini-table-wrap smart-flow-table-wrap smart-flow-recent-wrap">
              <table class="mini-table smart-flow-table">
                <thead>
                  <tr>
                    <th>{{ t('radar.time') }}</th>
                    <th>{{ t('radar.side') }}</th>
                    <th>{{ t('radar.wallet') }}</th>
                    <th>{{ t('radar.value') }}</th>
                    <th>{{ t('radar.flowQuality') }}</th>
                    <th>{{ t('radar.smart') }}</th>
                    <th>{{ t('radar.cluster') }}</th>
                    <th>TX</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in smartFlow.recent_flows.slice(0, 24)" :key="entry.flow.id">
                    <td>{{ formatLocalDateTime(entry.flow.occurred_at) }}</td>
                    <td><span :class="['flow-side-badge', `flow-side-badge--${entry.flow.event_kind}`]">{{ t(`radar.flowSides.${entry.flow.event_kind}`) }}</span></td>
                    <td><CopyableAddress :value="entry.flow.wallet_address" /></td>
                    <td>{{ formatMoney(entry.flow.value_usd) }}</td>
                    <td>{{ entry.flow.is_wash_suspected ? t('radar.suspected') : t('radar.organic') }}</td>
                    <td>{{ entry.smart ? t('radar.yes') : t('radar.no') }}</td>
                    <td>{{ entry.flow.cluster_id ? compactAddress(entry.flow.cluster_id) : '—' }}</td>
                    <td><code>{{ compactAddress(entry.flow.tx_hash) }}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="inline-empty">{{ t('radar.recentCanonicalFlowsEmpty') }}</div>

            <div class="history-footnote smart-flow-footnote">
              <span>{{ t('radar.smartThresholds') }} Q≥{{ formatScore(smartFlow.thresholds.quality_score) }} · C≥{{ formatRatio(smartFlow.thresholds.min_confidence) }} · V≥{{ formatRatio(smartFlow.thresholds.min_valuation_coverage) }}</span>
              <span>{{ t('radar.smartFlowDeterministic') }}</span>
            </div>
          </template>
          <div v-else class="inline-empty">{{ t('radar.smartFlowEmpty') }}</div>
        </section>

        <section class="detail-section crypto-graph-section">
          <div class="section-title holder-history-title">
            <Network :size="15" />
            <strong>{{ t('radar.cryptoGraphTitle') }}</strong>
            <span>{{ t('radar.cryptoGraphHint') }}</span>
          </div>
          <div class="holder-history-toolbar">
            <div class="holder-history-windows">
              <button
                v-for="option in cryptoGraphWindowOptions"
                :key="option.seconds"
                :class="['history-window-button', 'crypto-graph-window-button', { active: cryptoGraphWindowSeconds === option.seconds }]"
                :disabled="cryptoGraphLoading"
                @click="emit('crypto-graph-window', option.seconds)"
              >
                {{ option.label }}
              </button>
            </div>
            <span v-if="cryptoGraph">
              {{ t('radar.graphStats', {
                wallets: cryptoGraph.wallets.length,
                clusters: cryptoGraph.clusters.length,
                persons: cryptoGraph.persons.length,
                evidence: cryptoGraph.telegram_evidence.length,
                outcomes: cryptoGraphVerifiedOutcomeCount,
              }) }}
            </span>
          </div>
          <div v-if="cryptoGraphError" class="history-error"><AlertTriangle :size="13" />{{ cryptoGraphError }}</div>
          <div v-if="cryptoGraphLoading" class="inline-empty"><RefreshCcw :size="13" class="spinning" />{{ t('radar.cryptoGraphLoading') }}</div>
          <template v-else-if="cryptoGraph">
            <div v-if="cryptoGraph.wallet_truncated || cryptoGraph.evidence_truncated || cryptoGraph.flow_truncated" class="graph-truncation-warning">
              <AlertTriangle :size="13" />
              <span>{{ t('radar.cryptoGraphTruncated') }}</span>
            </div>

            <div class="crypto-graph-summary">
              <div><span>{{ t('radar.graphWallets') }}</span><strong>{{ cryptoGraph.wallets.length }}</strong></div>
              <div><span>{{ t('radar.graphClusters') }}</span><strong>{{ cryptoGraph.clusters.length }}</strong></div>
              <div><span>{{ t('radar.graphPersons') }}</span><strong>{{ cryptoGraph.persons.length }}</strong></div>
              <div><span>{{ t('radar.graphTelegramEvidence') }}</span><strong>{{ cryptoGraph.telegram_evidence.length }}</strong></div>
              <div><span>{{ t('radar.graphVerifiedOutcomes') }}</span><strong>{{ cryptoGraphVerifiedOutcomeCount }}</strong></div>
            </div>

            <div class="smart-flow-subtitle">
              <strong>{{ t('radar.graphIdentityPaths') }}</strong>
              <span>{{ t('radar.graphIdentityPathsHint') }}</span>
            </div>
            <div v-if="cryptoGraph.wallets.length" class="graph-path-list">
              <article v-for="wallet in cryptoGraph.wallets" :key="wallet.wallet_id" class="graph-path-card">
                <div class="graph-node graph-node--person">
                  <span class="graph-node-label">{{ t('radar.personIdentity') }}</span>
                  <template v-if="graphIdentityLinksForWallet(wallet.wallet_id).length">
                    <div
                      v-for="identity in graphIdentityLinksForWallet(wallet.wallet_id)"
                      :key="identity.id"
                      class="graph-identity-row"
                    >
                      <strong v-if="identity.identity_kind === 'telegram_sender'">{{ graphPersonLabel(identity.identity_key) }}</strong>
                      <strong v-else>{{ identity.identity_kind }} · {{ identity.identity_key }}</strong>
                      <small v-if="identity.identity_kind === 'telegram_sender'">{{ graphPersonSecondary(identity.identity_key) }}</small>
                      <small>{{ identity.source }} · {{ formatRatio(identity.confidence) }}</small>
                    </div>
                  </template>
                  <span v-else class="graph-node-empty">{{ t('radar.noExplicitIdentity') }}</span>
                </div>
                <span class="graph-arrow">→</span>
                <div class="graph-node graph-node--wallet">
                  <span class="graph-node-label">{{ t('radar.wallet') }}</span>
                  <strong><CopyableAddress :value="wallet.address" /></strong>
                  <small>{{ wallet.smart ? t('radar.smart') : t('radar.observed') }} · B/S {{ wallet.buy_count }}/{{ wallet.sell_count }}</small>
                  <small>{{ t('radar.organicFlow') }} {{ formatMoney(wallet.organic_buy_volume_usd - wallet.organic_sell_volume_usd) }}</small>
                </div>
                <span class="graph-arrow">→</span>
                <div class="graph-node graph-node--cluster">
                  <span class="graph-node-label">{{ t('radar.cluster') }}</span>
                  <template v-if="graphClusterForWallet(wallet.cluster_ids)">
                    <strong>{{ truncate(graphClusterForWallet(wallet.cluster_ids)!.fingerprint, 26) }}</strong>
                    <small>{{ t('radar.suspectedRelated') }} · {{ formatRatio(graphClusterForWallet(wallet.cluster_ids)!.confidence) }}</small>
                  </template>
                  <template v-else>
                    <strong>{{ t('radar.independentWallet') }}</strong>
                    <small>{{ t('radar.noClusterEvidence') }}</small>
                  </template>
                </div>
                <span class="graph-arrow">→</span>
                <div class="graph-node graph-node--token">
                  <span class="graph-node-label">{{ t('radar.token') }}</span>
                  <strong>{{ selected.symbol || selected.name || compactAddress(cryptoGraph.contract_address) }}</strong>
                  <small><CopyableAddress :value="cryptoGraph.contract_address" /></small>
                  <small>{{ cryptoGraph.chain_id }}</small>
                </div>
              </article>
            </div>
            <div v-else class="inline-empty">{{ t('radar.cryptoGraphNoWallets') }}</div>

            <div class="smart-flow-subtitle graph-evidence-heading">
              <strong>{{ t('radar.graphTelegramToOutcome') }}</strong>
              <span>{{ t('radar.graphTelegramToOutcomeHint') }}</span>
            </div>
            <div v-if="cryptoGraph.telegram_evidence.length" class="graph-evidence-list">
              <article v-for="evidence in cryptoGraph.telegram_evidence" :key="evidence.link_id" class="graph-evidence-card">
                <header>
                  <div>
                    <strong>{{ evidence.message.sender_name || evidence.message.sender_username || t('radar.unknownSender') }}</strong>
                    <span v-if="evidence.message.sender_username">@{{ evidence.message.sender_username }}</span>
                    <span>{{ evidence.message.channel_name }} · {{ evidence.message.channel_username || '—' }}</span>
                  </div>
                  <time>{{ formatLocalDateTime(evidence.message.published_at) }}</time>
                </header>
                <p class="graph-message-text">{{ evidence.message.text }}</p>
                <div class="graph-evidence-meta">
                  <span>{{ evidence.relation_type }}</span>
                  <span>{{ evidence.source }}</span>
                  <span>{{ formatRatio(evidence.confidence) }}</span>
                </div>

                <div v-if="evidence.alpha_signal" class="graph-signal-card graph-signal-card--alpha">
                  <div class="graph-signal-head">
                    <strong>Alpha · {{ evidence.alpha_signal.event_type }} · {{ evidence.alpha_signal.direction }}</strong>
                    <span>Integrity {{ formatScore(evidence.alpha_signal.integrity_score) }}</span>
                  </div>
                  <p>{{ evidence.alpha_signal.thesis }}</p>
                  <div v-if="evidence.alpha_outcomes.length" class="graph-outcome-list">
                    <span v-for="outcome in evidence.alpha_outcomes" :key="`${outcome.signal_id}:${outcome.horizon_seconds}`" class="graph-outcome-chip">
                      {{ graphOutcomeLabel(outcome.horizon_seconds) }}
                      <strong :class="{ positive: outcome.directional_return_pct > 0, negative: outcome.directional_return_pct < 0 }">{{ formatPercent(outcome.directional_return_pct) }}</strong>
                      <small>MFE {{ formatPercent(outcome.mfe_pct) }} · MAE {{ formatPercent(outcome.mae_pct) }} · {{ outcome.pricing_source }}</small>
                    </span>
                  </div>
                  <small v-else class="graph-node-empty">{{ t('radar.noVerifiedOutcome') }}</small>
                </div>

                <div v-if="evidence.intelligence_event" class="graph-signal-card graph-signal-card--intel">
                  <div class="graph-signal-head">
                    <strong>{{ t('radar.intelligenceEvent') }} · {{ evidence.intelligence_event.event_type }}</strong>
                    <span>{{ formatRatio(evidence.intelligence_event.confidence) }}</span>
                  </div>
                  <p><strong>{{ evidence.intelligence_event.headline }}</strong> · {{ evidence.intelligence_event.summary }}</p>
                  <small v-if="evidence.intelligence_event.speaker_name">{{ t('radar.speaker') }} {{ evidence.intelligence_event.speaker_name }}</small>
                </div>
              </article>
            </div>
            <div v-else class="inline-empty">{{ t('radar.cryptoGraphNoTelegramEvidence') }}</div>

            <div class="history-footnote graph-footnote">
              <span>{{ t('radar.cryptoGraphWhitelistScoped') }}</span>
              <span>{{ t('radar.cryptoGraphExplicitIdentityOnly') }}</span>
              <span>{{ t('radar.vectorNotFactEvidence') }}</span>
            </div>
          </template>
          <div v-else class="inline-empty">{{ t('radar.cryptoGraphEmpty') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title"><CircleDollarSign :size="15" /><strong>{{ t('radar.capitalFlow') }}</strong><span>{{ t('radar.organicVsRaw') }}</span></div>
          <div v-if="selected.radar_input" class="flow-grid">
            <div class="flow-card flow-card--buy"><ArrowUpRight :size="15" /><span>{{ t('radar.organicBuy5m') }}</span><strong>{{ formatMoney(selected.radar_input.flow.organic_buy_volume_5m_usd) }}</strong><small>{{ t('radar.raw') }} {{ formatMoney(selected.radar_input.flow.buy_volume_5m_usd) }}</small></div>
            <div class="flow-card flow-card--sell"><ArrowDownRight :size="15" /><span>{{ t('radar.organicSell5m') }}</span><strong>{{ formatMoney(selected.radar_input.flow.organic_sell_volume_5m_usd) }}</strong><small>{{ t('radar.raw') }} {{ formatMoney(selected.radar_input.flow.sell_volume_5m_usd) }}</small></div>
            <div class="flow-card"><Activity :size="15" /><span>{{ t('radar.organicNet5m') }}</span><strong>{{ formatMoney(selected.radar_input.flow.organic_net_inflow_5m_usd) }}</strong><small>{{ t('radar.smartNet') }} {{ formatMoney(selected.radar_input.flow.organic_smart_money_net_inflow_5m_usd) }}</small></div>
            <div class="flow-card"><Network :size="15" /><span>{{ t('radar.independentBuyers') }}</span><strong>{{ selected.radar_input.flow.independent_buyers_5m }}</strong><small>{{ t('radar.valuationCoverage') }} {{ formatRatio(selected.radar_input.flow.valuation_coverage_ratio_5m) }}</small></div>
            <div class="flow-card"><ShieldAlert :size="15" /><span>{{ t('radar.washRatio') }}</span><strong>{{ formatRatio(selected.radar_input.flow.wash_volume_ratio_5m) }}</strong><small>{{ t('radar.fundedCluster') }} {{ formatRatio(selected.radar_input.flow.funded_cluster_suspected_volume_ratio_5m) }}</small></div>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-title"><Network :size="15" /><strong>{{ t('radar.topHolderQuality') }}</strong><span>{{ t('radar.clusterDeduped') }}</span></div>
          <template v-if="selected.radar_input">
            <div class="top-holder-summary">
              <div><span>{{ t('radar.robustQuality') }}</span><strong>{{ formatScore(selected.radar_input.top_holders.robust_quality_score) }}</strong></div>
              <div><span>{{ t('radar.qualityCoverage') }}</span><strong>{{ formatRatio(selected.radar_input.top_holders.robust_quality_coverage_ratio) }}</strong></div>
              <div><span>{{ t('radar.top10Independent') }}</span><strong>{{ selected.radar_input.top_holders.independent_clusters_top10 }}</strong></div>
              <div><span>{{ t('radar.top25HighQuality') }}</span><strong>{{ selected.radar_input.top_holders.high_quality_top25 }}</strong></div>
            </div>
            <div v-if="selected.radar_input.top_holders.members.length" class="mini-table-wrap">
              <table class="mini-table">
                <thead><tr><th>#</th><th>{{ t('radar.wallet') }}</th><th>{{ t('radar.share') }}</th><th>{{ t('radar.quality') }}</th><th>{{ t('radar.cluster') }}</th></tr></thead>
                <tbody>
                  <tr v-for="member in selected.radar_input.top_holders.members" :key="`${member.rank}:${member.address}`">
                    <td>{{ member.rank }}</td><td><CopyableAddress :value="member.address" /></td><td>{{ formatRatio(member.balance_share) }}</td><td>{{ member.quality_score === null ? '—' : formatScore(member.quality_score) }}</td><td :title="member.independence_key">{{ truncate(member.independence_key, 22) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <Network :size="15" />
            <strong>{{ t('radar.walletClusterGraph') }}</strong>
            <span>{{ t('radar.walletClusterGraphHint') }}</span>
          </div>
          <div v-if="clustersError" class="cluster-error"><AlertTriangle :size="13" />{{ clustersError }}</div>
          <div v-if="clustersLoading" class="inline-empty"><RefreshCcw :size="13" class="spinning" />{{ t('radar.loadingClusters') }}</div>
          <div v-else-if="clusters.length" class="cluster-list">
            <article v-for="cluster in clusters" :key="cluster.id" class="cluster-card">
              <div class="cluster-card-head">
                <div>
                  <strong>{{ t('radar.suspectedRelatedCluster') }}</strong>
                  <code :title="cluster.fingerprint">{{ truncate(cluster.fingerprint, 30) }}</code>
                </div>
                <span>{{ t('radar.confidence') }} {{ formatRatio(cluster.confidence) }}</span>
              </div>
              <div class="cluster-stats">
                <div><span>{{ t('radar.clusterMembers') }}</span><strong>{{ cluster.members.length }}</strong></div>
                <div><span>{{ t('radar.clusterEdges') }}</span><strong>{{ clusterEdges(cluster).length }}</strong></div>
                <div><span>{{ t('radar.clusterEdgeDensity') }}</span><strong>{{ formatRatio(clusterMetric(cluster, 'edge_density')) }}</strong></div>
                <div><span>{{ t('radar.clusterLastObserved') }}</span><strong>{{ formatLocalDateTime(cluster.last_observed_at) }}</strong></div>
              </div>
              <div class="cluster-nodes">
                <div v-for="member in cluster.members" :key="member.wallet_id" class="cluster-node">
                  <span class="cluster-node-dot" />
                  <CopyableAddress :value="member.address" />
                  <small>{{ formatRatio(member.confidence) }}</small>
                </div>
              </div>
              <div v-if="clusterEdges(cluster).length" class="cluster-edges">
                <div v-for="(edge, index) in clusterEdges(cluster)" :key="`${cluster.id}:${index}`" class="cluster-edge">
                  <div>
                    <CopyableAddress :value="edge.wallets[0]" />
                    <span>↔</span>
                    <CopyableAddress :value="edge.wallets[1]" />
                    <strong>{{ formatRatio(edge.confidence) }}</strong>
                  </div>
                  <small>{{ evidencePreview(edge.features) }}</small>
                </div>
              </div>
              <div v-if="clusterLimitations(cluster).length" class="cluster-limitations">
                <span v-for="limitation in clusterLimitations(cluster)" :key="limitation">{{ t(`radar.clusterLimitations.${limitation}`) }}</span>
              </div>
            </article>
          </div>
          <div v-else class="inline-empty">{{ t('radar.noWalletClusters') }}</div>
          <p class="cluster-disclaimer">{{ t('radar.walletClusterDisclaimer') }}</p>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <ShieldAlert :size="15" />
            <strong>{{ t('radar.scamGuard') }}</strong>
            <span>{{ t('radar.scamGuardHint') }}</span>
          </div>
          <template v-if="selected.evaluation?.scam_guard">
            <div class="scam-summary">
              <div>
                <span>{{ t('radar.scamLevel') }}</span>
                <strong><span :class="scamClass(selected.evaluation.scam_guard.level)">{{ t(`radar.scamLevels.${selected.evaluation.scam_guard.level}`) }}</span></strong>
              </div>
              <div><span>{{ t('radar.scamScore') }}</span><strong>{{ formatScore(selected.evaluation.scam_guard.score) }} / 100</strong></div>
              <div><span>{{ t('radar.confidence') }}</span><strong>{{ formatRatio(selected.evaluation.scam_guard.confidence) }}</strong></div>
              <div><span>{{ t('radar.triggered') }}</span><strong>{{ selected.evaluation.scam_guard.triggered ? t('radar.yes') : t('radar.no') }}</strong></div>
            </div>

            <div v-if="selected.evaluation.scam_guard.social" class="scam-social-grid">
              <article v-if="selected.evaluation.scam_guard.social.fomo" class="scam-social-card">
                <div><strong>FomoScan</strong><span>{{ t('radar.coverage') }} {{ formatRatio(selected.evaluation.scam_guard.social.coverage_ratio) }}</span></div>
                <dl>
                  <div><dt>{{ t('radar.fomoIdentities') }}</dt><dd>{{ selected.evaluation.scam_guard.social.fomo.identity_count.toLocaleString() }}</dd></div>
                  <div><dt>{{ t('radar.fomoHolderRatio') }}</dt><dd>{{ formatRatio(selected.evaluation.scam_guard.social.fomo.recognized_holder_ratio) }}</dd></div>
                  <div><dt>{{ t('radar.fomoResets') }}</dt><dd>{{ selected.evaluation.scam_guard.social.fomo.reset_count ?? '—' }}</dd></div>
                </dl>
              </article>
              <article v-if="selected.evaluation.scam_guard.social.x" class="scam-social-card">
                <div><strong>X / Twitter</strong><span>{{ selected.evaluation.scam_guard.social.x.username ? `@${selected.evaluation.scam_guard.social.x.username}` : selected.evaluation.scam_guard.social.x.account_id }}</span></div>
                <dl>
                  <div><dt>{{ t('radar.accountCreated') }}</dt><dd>{{ selected.evaluation.scam_guard.social.x.created_at ? formatLocalDateTime(selected.evaluation.scam_guard.social.x.created_at) : '—' }}</dd></div>
                  <div><dt>{{ t('radar.followersFollowing') }}</dt><dd>{{ selected.evaluation.scam_guard.social.x.followers_count ?? '—' }} / {{ selected.evaluation.scam_guard.social.x.following_count ?? '—' }}</dd></div>
                  <div><dt>{{ t('radar.postCount') }}</dt><dd>{{ selected.evaluation.scam_guard.social.x.post_count ?? '—' }}</dd></div>
                  <div><dt>{{ t('radar.contractIdentity') }}</dt><dd>{{ contractMatchLabel(selected.evaluation.scam_guard.social.x.exact_contract_match) }}</dd></div>
                </dl>
              </article>
            </div>

            <div v-if="selected.evaluation.scam_guard.factors.length" class="scam-factors">
              <article v-for="factor in selected.evaluation.scam_guard.factors" :key="factor.key" class="risk-check">
                <div><strong>{{ scamFactorLabel(factor.key) }}</strong><span class="scam-factor-score">+{{ formatScore(factor.score) }}</span></div>
                <small>{{ t('radar.confidence') }} {{ formatRatio(factor.confidence) }}</small>
                <code>{{ evidencePreview(factor.evidence) }}</code>
              </article>
            </div>
            <div v-else class="inline-empty">{{ t('radar.noScamFactors') }}</div>
            <p class="scam-disclaimer">{{ t('radar.scamDisclaimer') }}</p>
          </template>
          <div v-else class="inline-empty">{{ t('radar.scamGuardUnavailable') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <Activity :size="15" />
            <strong>{{ t('radar.outcomeReview') }}</strong>
            <span>{{ t('radar.outcomeReviewHint') }}</span>
          </div>
          <div v-if="selected.outcome_summary.trigger_count > 0" class="outcome-summary">
            <div>
              <span>{{ t('radar.outcomeTriggers') }}</span>
              <strong>{{ selected.outcome_summary.trigger_count }}</strong>
              <small>{{ t('radar.outcomePriced', { count: selected.outcome_summary.priced_trigger_count }) }}</small>
            </div>
            <div><span>{{ t('radar.outcomeFinalized') }}</span><strong>{{ selected.outcome_summary.finalized_trigger_count }}</strong></div>
            <div><span>{{ t('radar.outcomeVerified') }}</span><strong>{{ selected.outcome_summary.verified_trigger_count }}</strong></div>
            <div><span>{{ t('radar.outcomeNearZero') }}</span><strong>{{ selected.outcome_summary.near_zero_count }}</strong></div>
            <div><span>{{ t('radar.outcomeRecovered') }}</span><strong>{{ selected.outcome_summary.recovered_count }}</strong></div>
            <div><span>{{ t('radar.outcomeRebreak') }}</span><strong>{{ selected.outcome_summary.rebreak_count }}</strong></div>
            <div><span>{{ t('radar.outcomeSuccess') }}</span><strong>{{ selected.outcome_summary.successful_breakout_count }}</strong></div>
            <div><span>{{ t('radar.outcomeBestMfe') }}</span><strong>{{ formatPercent(selected.outcome_summary.best_mfe_pct) }}</strong></div>
            <div><span>{{ t('radar.outcomeWorstMae') }}</span><strong>{{ formatPercent(selected.outcome_summary.worst_mae_pct) }}</strong></div>
            <div>
              <span>{{ t('radar.outcomeLatestTrigger') }}</span>
              <strong class="outcome-time">{{ selected.outcome_summary.latest_trigger_at ? formatLocalDateTime(selected.outcome_summary.latest_trigger_at) : '—' }}</strong>
            </div>
          </div>
          <div v-else class="inline-empty">{{ t('radar.outcomeEmpty') }}</div>
          <p class="outcome-disclaimer">{{ t('radar.outcomeDisclaimer') }}</p>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <component :is="selected.episode.risk_status === 'hard_risk' ? ShieldAlert : ShieldCheck" :size="15" />
            <strong>{{ t('radar.riskGate') }}</strong>
            <span v-if="selected.risk_snapshot">{{ t('radar.coverage') }} {{ formatRatio(selected.risk_snapshot.coverage_ratio) }}</span>
          </div>
          <div v-if="selected.risk_snapshot" class="risk-checks">
            <article v-for="check in selected.risk_snapshot.checks" :key="check.key" class="risk-check">
              <div><strong>{{ check.key }}</strong><span :class="['check-status', `check-status--${check.status}`]">{{ riskCheckLabel(check) }}</span></div>
              <small>{{ t('radar.confidence') }} {{ formatRatio(check.confidence) }}<template v-if="check.flag"> · {{ riskFlagLabel(check.flag) }}</template></small>
              <code>{{ evidencePreview(check.evidence) }}</code>
            </article>
            <div v-if="selected.risk_snapshot.risk_flags.length" class="risk-flags"><span v-for="flag in selected.risk_snapshot.risk_flags" :key="flag">{{ riskFlagLabel(flag) }}</span></div>
          </div>
          <div v-else class="inline-empty">{{ t('radar.riskProvenanceMissing') }}</div>
        </section>

        <section class="detail-section">
          <div class="section-title"><Clock3 :size="15" /><strong>{{ t('radar.episodeTimeline') }}</strong><span>{{ selected.episode.event_count }} {{ t('radar.events') }}</span></div>
          <div class="timeline-list">
            <article v-for="event in timeline" :key="event.id" class="timeline-event">
              <div class="timeline-marker"><CheckCircle2 :size="13" /></div>
              <div>
                <div class="timeline-title">
                  <strong>{{ event.from_state ? t(`radar.states.${event.from_state}`) : t('radar.episodeStart') }} → {{ t(`radar.states.${event.to_state}`) }}</strong>
                  <span>{{ formatLocalDateTime(event.observed_at) }}</span>
                </div>
                <p>{{ t('radar.score') }} {{ formatScore(event.score) }} · {{ t(`radar.risks.${event.risk_status}`) }}<template v-if="evidenceReason(event)"> · {{ evidenceReason(event) }}</template></p>
                <small v-if="event.notification_candidate">{{ event.notification_sent ? t('radar.notificationSent') : t('radar.notificationPending') }}</small>
              </div>
            </article>
            <div v-if="timelineLoading" class="inline-empty"><RefreshCcw :size="13" class="spinning" />{{ t('radar.loadingTimeline') }}</div>
            <div v-else-if="timeline.length === 0" class="inline-empty">{{ t('radar.noTimeline') }}</div>
          </div>
          <button v-if="timelineHasMore" class="radar-button timeline-more" :disabled="timelineLoading" @click="emit('load-more-events')"><ChevronDown :size="14" />{{ t('radar.loadOlderEvents') }}</button>
        </section>

        <section class="detail-footnote">
          <ExternalLink :size="13" />
          <span>{{ t('radar.provenanceFootnote', { source: selected.discovery_source, chain: selected.chain_id }) }}</span>
        </section>
      </aside>

      <aside v-else class="radar-detail-placeholder">
        <Radar :size="28" />
        <strong>{{ t('radar.selectAsset') }}</strong>
        <p>{{ t('radar.selectAssetHint') }}</p>
      </aside>
    </div>
  </section>
</template>

<style scoped>
.radar-workspace { display: flex; min-height: 0; flex: 1; flex-direction: column; gap: 12px; padding: 22px 24px 28px; overflow: auto; color: var(--tm-text, #e8e9ed); }
.radar-hero { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.radar-kicker { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; color: #9da7ff; font-size: 11px; font-weight: 750; letter-spacing: .12em; }
.radar-hero h1 { margin: 0; font-size: 27px; line-height: 1.15; letter-spacing: -.025em; }
.radar-hero p { max-width: 850px; margin: 7px 0 0; color: #9096a5; font-size: 13px; line-height: 1.55; }
.radar-health { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 7px; }
.health-chip { display: inline-flex; min-height: 30px; align-items: center; gap: 6px; border: 1px solid #2a2d36; border-radius: 8px; background: #17191f; padding: 0 9px; color: #8f95a4; font-size: 11px; white-space: nowrap; }
.health-chip.healthy { border-color: rgba(86, 210, 151, .25); color: #8edbb4; }
.health-chip.degraded { border-color: rgba(230, 171, 79, .32); background: rgba(159, 104, 29, .08); color: #dfb56e; }
.provider-runtime-strip { display: grid; grid-template-columns: 120px 110px minmax(150px, .8fr) minmax(150px, .8fr) minmax(220px, 1.4fr); gap: 5px; border: 1px solid #252932; border-radius: 8px; background: #14161b; padding: 7px 8px; }
.provider-runtime-strip > div { min-width: 0; border-right: 1px solid #232630; padding: 1px 8px; }
.provider-runtime-strip > div:last-child { border-right: 0; }
.provider-runtime-strip span { display: block; color: #676f7d; font-size: 8px; }
.provider-runtime-strip strong { display: block; margin-top: 2px; overflow: hidden; color: #afb5c1; font-size: 9px; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
.provider-runtime-strip .provider-runtime-error strong { color: #d6a56c; }
.radar-icon-button { display: inline-grid; width: 32px; height: 32px; place-items: center; border: 1px solid #2b2e38; border-radius: 8px; background: #191b22; color: #b7bdca; cursor: pointer; }
.radar-icon-button:hover { border-color: #4b55a5; color: #d9ddff; }
.radar-icon-button:disabled { opacity: .45; cursor: default; }
.radar-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.radar-summary-card { min-width: 0; border: 1px solid #262932; border-radius: 10px; background: #15171c; padding: 11px 13px; }
.radar-summary-card span { display: block; color: #858b99; font-size: 11px; }
.radar-summary-card strong { display: block; margin-top: 3px; font-size: 20px; line-height: 1.1; }
.radar-summary-card small { display: block; margin-top: 4px; overflow: hidden; color: #676d7a; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.radar-filter-bar { display: grid; grid-template-columns: minmax(180px, 1.3fr) 150px minmax(170px, 1fr) 145px 145px auto; align-items: end; gap: 8px; border: 1px solid #262932; border-radius: 10px; background: #15171c; padding: 10px; }
.filter-field { min-width: 0; }
.filter-field label { display: block; margin: 0 0 5px 2px; color: #777e8e; font-size: 10px; font-weight: 650; }
.filter-field input, .filter-field select { box-sizing: border-box; width: 100%; height: 34px; border: 1px solid #30333d; border-radius: 7px; outline: none; background: #1b1d24; padding: 0 9px; color: #d9dce4; font: inherit; font-size: 12px; }
.filter-field input:focus, .filter-field select:focus { border-color: #5965c2; box-shadow: 0 0 0 2px rgba(88, 99, 193, .12); }
.filter-field input:disabled { color: #747a88; }
.input-shell { position: relative; }
.input-shell svg { position: absolute; top: 10px; left: 9px; color: #686f7e; }
.input-shell input { padding-left: 29px; }
.filter-actions { display: flex; gap: 6px; }
.radar-button { display: inline-flex; min-height: 34px; align-items: center; justify-content: center; gap: 6px; border: 1px solid #30333d; border-radius: 7px; background: #1b1d24; padding: 0 10px; color: #aeb4c1; font-size: 11px; font-weight: 650; cursor: pointer; white-space: nowrap; }
.radar-button:hover { border-color: #515a9f; color: #d8dcff; }
.radar-button--primary { border-color: #505cc0; background: #343c86; color: #eff0ff; }
.radar-button--primary span { min-width: 18px; border-radius: 9px; background: rgba(255,255,255,.12); padding: 1px 5px; font-size: 9px; }
.radar-button:disabled { opacity: .45; cursor: default; }
.radar-error { display: flex; align-items: center; gap: 8px; border: 1px solid rgba(239, 106, 118, .25); border-radius: 8px; background: rgba(150, 44, 55, .12); padding: 9px 11px; color: #f0a3aa; font-size: 12px; }
.radar-layout { display: grid; min-height: 560px; grid-template-columns: minmax(0, 1.7fr) minmax(390px, .9fr); align-items: start; gap: 10px; }
.radar-list-panel, .radar-detail-panel, .radar-detail-placeholder { min-width: 0; border: 1px solid #262932; border-radius: 10px; background: #14161b; }
.panel-heading { display: flex; min-height: 48px; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 1px solid #24272f; padding: 0 12px; }
.panel-heading div { display: flex; min-width: 0; flex-direction: column; gap: 2px; }
.panel-heading strong { font-size: 12px; }
.panel-heading small, .panel-heading > span { color: #6e7481; font-size: 10px; }
.radar-table-wrap { overflow: auto; }
.radar-table { width: 100%; min-width: 1180px; border-collapse: collapse; font-size: 11px; }
.radar-table th { position: sticky; top: 0; z-index: 1; height: 31px; border-bottom: 1px solid #262932; background: #17191f; padding: 0 8px; color: #737a89; font-size: 9px; font-weight: 700; letter-spacing: .03em; text-align: left; white-space: nowrap; }
.radar-table td { height: 52px; border-bottom: 1px solid #21242b; padding: 4px 8px; color: #b7bdc9; vertical-align: middle; }
.radar-table tbody tr { cursor: pointer; transition: background .12s ease; }
.radar-table tbody tr:hover, .radar-table tbody tr.selected { background: #1b1f2a; }
.radar-table tbody tr.selected { box-shadow: inset 2px 0 #7380e8; }
.radar-table .numeric { text-align: right; font-variant-numeric: tabular-nums; }
.token-cell strong, .token-cell span, .token-cell small { display: block; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.token-cell strong { color: #e3e5ea; font-size: 12px; }
.token-cell span { margin-top: 2px; color: #808797; font-size: 10px; }
.token-cell small { margin-top: 2px; color: #555c69; font-size: 9px; }
.score-cell { color: #d8dcff !important; font-size: 13px; font-weight: 750; }
.time-cell { min-width: 142px; max-width: none; color: #707786 !important; font-size: 9px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.metric-delta.positive { color: #7fd5a5; }
.metric-delta.negative { color: #e08b96; }
.radar-state, .radar-risk, .scam-risk { display: inline-flex; min-height: 21px; align-items: center; border: 1px solid transparent; border-radius: 999px; padding: 0 7px; font-size: 9px; font-weight: 750; letter-spacing: .02em; white-space: nowrap; }
.radar-state--discovered, .radar-state--candidate { border-color: #343946; color: #9ca3b2; }
.radar-state--watch { border-color: rgba(99, 113, 214, .35); background: rgba(75, 87, 173, .12); color: #aeb6ff; }
.radar-state--hot { border-color: rgba(224, 164, 80, .35); background: rgba(180, 113, 34, .12); color: #e9ba77; }
.radar-state--urgent { border-color: rgba(239, 105, 118, .4); background: rgba(184, 55, 68, .15); color: #f2a2aa; }
.radar-state--cooled { border-color: rgba(82, 193, 151, .25); color: #81cfae; }
.radar-state--invalidated { border-color: rgba(239, 105, 118, .35); color: #e48b94; text-decoration: line-through; }
.radar-risk--clean { border-color: rgba(88, 194, 144, .22); color: #86d2aa; }
.radar-risk--elevated { border-color: rgba(225, 169, 82, .3); color: #ddb36f; }
.radar-risk--hard_risk { border-color: rgba(235, 91, 105, .35); background: rgba(172, 46, 58, .12); color: #ef939d; }
.scam-risk--unknown { border-color: #343946; color: #8c93a1; }
.scam-risk--low { border-color: rgba(88, 194, 144, .22); color: #86d2aa; }
.scam-risk--medium { border-color: rgba(225, 169, 82, .3); background: rgba(180, 113, 34, .08); color: #ddb36f; }
.scam-risk--high { border-color: rgba(235, 119, 91, .35); background: rgba(172, 79, 46, .12); color: #eda07f; }
.scam-risk--critical { border-color: rgba(235, 91, 105, .4); background: rgba(172, 46, 58, .14); color: #ef939d; }
.empty-cell { height: 160px !important; color: #686f7e !important; text-align: center !important; }
.table-footer { display: flex; min-height: 46px; align-items: center; justify-content: space-between; gap: 10px; padding: 6px 10px; color: #656c7a; font-size: 10px; }
.table-footer span { display: inline-flex; align-items: center; gap: 6px; }
.radar-detail-panel { position: sticky; top: 0; max-height: calc(100vh - 120px); overflow: auto; }
.detail-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; border-bottom: 1px solid #262932; padding: 13px; }
.detail-title-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.detail-title-line h2 { margin: 0 4px 0 0; font-size: 18px; }
.detail-header p { margin: 5px 0 4px; color: #7b8290; font-size: 11px; }
.detail-header code { color: #626a78; font-size: 9px; word-break: break-all; }
.detail-meta-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); border-bottom: 1px solid #262932; }
.detail-meta-grid div { min-width: 0; border-right: 1px solid #22252c; border-bottom: 1px solid #22252c; padding: 8px 10px; }
.detail-meta-grid div:nth-child(3n) { border-right: 0; }
.detail-meta-grid span, .metric-grid span, .top-holder-summary span { display: block; color: #707786; font-size: 9px; }
.detail-meta-grid strong { display: block; margin-top: 3px; overflow: hidden; color: #d7dae1; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.detail-section { border-bottom: 1px solid #262932; padding: 11px 12px; }
.section-title { display: flex; align-items: center; gap: 6px; margin-bottom: 9px; color: #a5acbc; }
.section-title strong { color: #d5d8df; font-size: 11px; }
.section-title span { margin-left: auto; color: #666d7a; font-size: 9px; }
.score-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; }
.score-part { border: 1px solid #242730; border-radius: 6px; background: #17191f; padding: 7px; }
.score-part span { display: block; min-height: 22px; color: #6e7584; font-size: 8px; line-height: 1.3; }
.score-part strong { display: block; margin-top: 2px; color: #c8cdf6; font-size: 13px; }
.score-part strong.penalty { color: #df8f98; }
.metric-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 5px; }
.metric-grid > div { min-width: 0; border: 1px solid #23262e; border-radius: 6px; padding: 7px; }
.metric-grid strong { display: block; margin-top: 3px; color: #d3d6de; font-size: 12px; font-variant-numeric: tabular-nums; }
.metric-grid small { display: block; margin-top: 2px; color: #606775; font-size: 8px; }
.holder-history-title { margin-bottom: 7px; }
.holder-history-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 7px; }
.holder-history-toolbar > span { overflow: hidden; color: #626a78; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.holder-history-windows { display: inline-flex; gap: 3px; border: 1px solid #252a34; border-radius: 7px; background: #15181e; padding: 3px; }
.history-window-button { min-width: 34px; height: 24px; border: 0; border-radius: 5px; background: transparent; color: #6d7584; font-size: 8px; font-weight: 700; cursor: pointer; }
.history-window-button:hover { color: #c4c9d4; }
.history-window-button.active { background: #2d355f; color: #dfe3ff; }
.history-window-button:disabled { opacity: .5; cursor: default; }
.history-error { display: flex; align-items: center; gap: 6px; margin-bottom: 7px; border: 1px solid rgba(224, 168, 82, .22); border-radius: 6px; padding: 6px 7px; color: #d8b778; font-size: 9px; }
.holder-history-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.history-card { min-width: 0; border: 1px solid #252934; border-radius: 7px; background: #17191f; padding: 8px; }
.history-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.history-card-head > div { min-width: 0; }
.history-card-head span { display: block; color: #707887; font-size: 8px; }
.history-card-head strong { display: block; margin-top: 2px; color: #d2d6df; font-size: 13px; font-variant-numeric: tabular-nums; }
.history-card-head small { color: #747d8c; font-size: 8px; white-space: nowrap; }
.history-chart { display: block; width: 100%; height: 76px; margin-top: 5px; overflow: visible; }
.history-grid-line { stroke: #272c36; stroke-width: 1; vector-effect: non-scaling-stroke; }
.history-line { fill: none; stroke-width: 1.7; vector-effect: non-scaling-stroke; stroke-linecap: round; stroke-linejoin: round; }
.history-line--holder { stroke: #9aa6ff; }
.history-line--organic { stroke: #76cda0; }
.history-line--recognized { stroke: #d9ae69; }
.history-line--smart { stroke: #a58bea; }
.history-line--concentration { stroke: #dd858f; }
.history-legend { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 3px; color: #68717f; font-size: 7px; }
.history-legend span::before { display: inline-block; width: 7px; height: 2px; margin-right: 4px; border-radius: 2px; background: #68717f; vertical-align: middle; content: ''; }
.history-legend .legend-holder::before { background: #9aa6ff; }
.history-legend .legend-organic::before { background: #76cda0; }
.history-legend .legend-recognized::before { background: #d9ae69; }
.history-legend .legend-smart::before { background: #a58bea; }
.history-legend .legend-concentration::before { background: #dd858f; }
.history-footnote { display: flex; flex-wrap: wrap; gap: 5px 12px; margin-top: 7px; color: #5e6674; font-size: 8px; line-height: 1.5; }
.smart-flow-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.smart-flow-summary > div { min-width: 0; border: 1px solid #242832; border-radius: 7px; background: #17191f; padding: 7px; }
.smart-flow-summary span { display: block; color: #6d7584; font-size: 8px; }
.smart-flow-summary strong { display: block; margin-top: 3px; color: #d2d6df; font-size: 12px; font-variant-numeric: tabular-nums; }
.smart-flow-summary strong.positive { color: #82cfa4; }
.smart-flow-summary strong.negative { color: #dc8b94; }
.smart-flow-summary small { display: block; margin-top: 2px; color: #5e6675; font-size: 8px; }
.smart-flow-charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.smart-flow-line--buy { stroke: #79cda0; }
.smart-flow-line--sell { stroke: #dd858f; }
.smart-flow-line--net { stroke: #a58bea; }
.history-legend .legend-organic-buy::before { background: #79cda0; }
.history-legend .legend-organic-sell::before { background: #dd858f; }
.history-legend .legend-smart-net::before { background: #a58bea; }
.smart-flow-subtitle { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin: 10px 0 6px; }
.smart-flow-subtitle strong { color: #b8bec9; font-size: 9px; }
.smart-flow-subtitle span { color: #5f6774; font-size: 8px; text-align: right; }
.smart-flow-subtitle--flows { margin-top: 11px; }
.smart-flow-table-wrap { max-height: 280px; }
.smart-flow-recent-wrap { max-height: 250px; }
.smart-flow-table td { white-space: nowrap; }
.smart-flow-table td small { color: #606978; font-size: 7px; }
.smart-wallet-badge, .flow-side-badge { display: inline-flex; align-items: center; border: 1px solid #303541; border-radius: 999px; padding: 2px 5px; color: #747d8c; font-size: 7px; font-weight: 700; }
.smart-wallet-badge.active { border-color: rgba(165, 139, 234, .34); background: rgba(165, 139, 234, .08); color: #b8a6ef; }
.flow-side-badge--buy { border-color: rgba(121, 205, 160, .32); background: rgba(121, 205, 160, .07); color: #83d4aa; }
.flow-side-badge--sell { border-color: rgba(221, 133, 143, .32); background: rgba(221, 133, 143, .07); color: #e09aa2; }
.smart-flow-footnote { border-top: 1px dashed #252a32; padding-top: 7px; }
.graph-truncation-warning { display: flex; align-items: center; gap: 6px; margin-bottom: 7px; border: 1px solid rgba(224, 168, 82, .24); border-radius: 7px; background: rgba(224, 168, 82, .05); padding: 6px 8px; color: #d8b778; font-size: 8px; }
.crypto-graph-summary { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.crypto-graph-summary > div { min-width: 0; border: 1px solid #242832; border-radius: 7px; background: #17191f; padding: 7px; }
.crypto-graph-summary span { display: block; color: #6d7584; font-size: 8px; }
.crypto-graph-summary strong { display: block; margin-top: 3px; color: #d2d6df; font-size: 13px; font-variant-numeric: tabular-nums; }
.graph-path-list, .graph-evidence-list { display: flex; flex-direction: column; gap: 7px; }
.graph-path-card { display: grid; grid-template-columns: minmax(150px, 1.35fr) auto minmax(130px, 1fr) auto minmax(130px, 1fr) auto minmax(130px, 1fr); align-items: stretch; gap: 5px; }
.graph-node { min-width: 0; border: 1px solid #262a34; border-radius: 7px; background: #17191f; padding: 7px 8px; }
.graph-node--person { border-color: rgba(165, 139, 234, .23); }
.graph-node--wallet { border-color: rgba(121, 205, 160, .20); }
.graph-node--cluster { border-color: rgba(224, 168, 82, .20); }
.graph-node--token { border-color: rgba(137, 146, 230, .24); }
.graph-node-label { display: block; margin-bottom: 4px; color: #626b79; font-size: 7px; text-transform: uppercase; letter-spacing: .06em; }
.graph-node strong { display: block; overflow: hidden; color: #c8cdd7; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.graph-node small { display: block; margin-top: 3px; overflow: hidden; color: #68717f; font-size: 7px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.graph-node code { color: inherit; font-size: inherit; }
.graph-identity-row + .graph-identity-row { margin-top: 6px; border-top: 1px dashed #292d35; padding-top: 5px; }
.graph-node-empty { color: #606978; font-size: 8px; }
.graph-arrow { align-self: center; color: #464e5b; font-size: 13px; }
.graph-evidence-heading { margin-top: 12px; }
.graph-evidence-card { border: 1px solid #272b34; border-radius: 8px; background: #17191f; padding: 8px; }
.graph-evidence-card > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.graph-evidence-card > header > div { display: flex; min-width: 0; flex-wrap: wrap; align-items: baseline; gap: 3px 6px; }
.graph-evidence-card > header strong { color: #d0d4dc; font-size: 9px; }
.graph-evidence-card > header span { color: #707987; font-size: 7px; }
.graph-evidence-card > header time { flex: 0 0 auto; color: #5f6875; font-size: 7px; }
.graph-message-text { margin: 6px 0 0; color: #9da5b3; font-size: 9px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; }
.graph-evidence-meta { display: flex; flex-wrap: wrap; gap: 4px 7px; margin-top: 6px; }
.graph-evidence-meta span { border: 1px solid #2b3039; border-radius: 999px; padding: 2px 5px; color: #707987; font-size: 7px; }
.graph-signal-card { margin-top: 7px; border: 1px solid #262c36; border-radius: 6px; padding: 7px; }
.graph-signal-card--alpha { background: rgba(121, 205, 160, .035); }
.graph-signal-card--intel { background: rgba(137, 146, 230, .035); }
.graph-signal-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.graph-signal-head strong { color: #bfc5d0; font-size: 8px; }
.graph-signal-head span { color: #777f8d; font-size: 7px; }
.graph-signal-card p { margin: 4px 0 0; color: #8f98a6; font-size: 8px; line-height: 1.45; }
.graph-signal-card > small { display: block; margin-top: 4px; color: #626b78; font-size: 7px; }
.graph-outcome-list { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }
.graph-outcome-chip { display: grid; grid-template-columns: auto auto; column-gap: 5px; align-items: baseline; border: 1px solid #2c323b; border-radius: 6px; padding: 4px 6px; color: #737c89; font-size: 7px; }
.graph-outcome-chip strong { color: #c7ccd6; font-size: 9px; }
.graph-outcome-chip strong.positive { color: #82cfa4; }
.graph-outcome-chip strong.negative { color: #dc8b94; }
.graph-outcome-chip small { grid-column: 1 / -1; margin-top: 2px; color: #5f6875; font-size: 6px; }
.graph-footnote { border-top: 1px dashed #252a32; padding-top: 7px; }
.flow-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px; }
.flow-card { display: grid; grid-template-columns: auto 1fr; align-items: center; column-gap: 7px; border: 1px solid #252832; border-radius: 7px; padding: 8px; }
.flow-card svg { grid-row: 1 / span 2; color: #8992e6; }
.flow-card span { color: #727988; font-size: 8px; }
.flow-card strong { color: #d5d8e1; font-size: 12px; font-variant-numeric: tabular-nums; }
.flow-card small { grid-column: 2; color: #5f6673; font-size: 8px; }
.flow-card--buy svg { color: #78cfa2; } .flow-card--sell svg { color: #db858f; }
.top-holder-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.top-holder-summary div { border: 1px solid #242730; border-radius: 6px; padding: 7px; }
.top-holder-summary strong { display: block; margin-top: 3px; font-size: 12px; }
.cluster-error { display: flex; align-items: center; gap: 6px; margin-bottom: 7px; border: 1px solid rgba(224, 168, 82, .22); border-radius: 6px; padding: 6px 7px; color: #d8b778; font-size: 9px; }
.cluster-list { display: flex; flex-direction: column; gap: 7px; }
.cluster-card { border: 1px solid #262a34; border-radius: 8px; background: #17191f; padding: 8px; }
.cluster-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.cluster-card-head > div { min-width: 0; }
.cluster-card-head strong { display: block; color: #d0d4dd; font-size: 10px; }
.cluster-card-head code { display: block; max-width: 260px; margin-top: 3px; overflow: hidden; color: #5f6878; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.cluster-card-head > span { border: 1px solid rgba(129, 143, 232, .25); border-radius: 999px; padding: 3px 6px; color: #a7afe9; font-size: 8px; white-space: nowrap; }
.cluster-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 4px; margin-top: 7px; }
.cluster-stats > div { min-width: 0; border: 1px solid #232731; border-radius: 5px; padding: 5px 6px; }
.cluster-stats span { display: block; color: #697180; font-size: 7px; }
.cluster-stats strong { display: block; margin-top: 2px; overflow: hidden; color: #bdc3cf; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.cluster-nodes { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 7px; }
.cluster-node { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #29303d; border-radius: 999px; background: #1b1f27; padding: 4px 6px; }
.cluster-node-dot { width: 5px; height: 5px; border-radius: 50%; background: #8994df; box-shadow: 0 0 0 2px rgba(137, 148, 223, .12); }
.cluster-node code { color: #929aaa; font-size: 8px; }
.cluster-node small { color: #667080; font-size: 7px; }
.cluster-edges { display: flex; flex-direction: column; gap: 4px; margin-top: 7px; }
.cluster-edge { border-left: 2px solid #39415c; border-radius: 0 5px 5px 0; background: #15181e; padding: 5px 6px; }
.cluster-edge > div { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; }
.cluster-edge code { color: #8891a1; font-size: 8px; }
.cluster-edge span { color: #626b7b; font-size: 9px; }
.cluster-edge strong { margin-left: auto; color: #a7afe9; font-size: 8px; }
.cluster-edge small { display: block; margin-top: 3px; overflow: hidden; color: #505968; font-size: 7px; text-overflow: ellipsis; white-space: nowrap; }
.cluster-limitations { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 7px; }
.cluster-limitations span { border: 1px solid rgba(219, 167, 87, .2); border-radius: 999px; padding: 3px 6px; color: #af9062; font-size: 7px; }
.cluster-disclaimer { margin: 7px 0 0; color: #5d6573; font-size: 8px; line-height: 1.5; }
.scam-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.scam-summary > div { min-width: 0; border: 1px solid #242730; border-radius: 6px; background: #17191f; padding: 7px; }
.scam-summary > div > span { display: block; color: #707786; font-size: 8px; }
.scam-summary > div > strong { display: block; margin-top: 4px; color: #d4d7df; font-size: 11px; }
.outcome-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; }
.outcome-summary > div { min-width: 0; border: 1px solid #242730; border-radius: 6px; background: #17191f; padding: 7px; }
.outcome-summary span { display: block; color: #707786; font-size: 8px; }
.outcome-summary strong { display: block; margin-top: 4px; color: #d4d7df; font-size: 12px; font-variant-numeric: tabular-nums; }
.outcome-summary small { display: block; margin-top: 2px; color: #616876; font-size: 8px; }
.outcome-summary .outcome-time { overflow: hidden; color: #9ca3b2; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.outcome-disclaimer { margin: 7px 0 0; color: #5f6674; font-size: 8px; line-height: 1.5; }
.scam-social-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.scam-social-card { min-width: 0; border: 1px solid #252832; border-radius: 7px; background: #17191f; padding: 8px; }
.scam-social-card > div { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-bottom: 6px; }
.scam-social-card > div strong { color: #d4d7df; font-size: 10px; }
.scam-social-card > div span { overflow: hidden; color: #6d7482; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.scam-social-card dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px; margin: 0; }
.scam-social-card dl div { min-width: 0; border-top: 1px solid #22252c; padding-top: 4px; }
.scam-social-card dt { color: #606775; font-size: 7px; }
.scam-social-card dd { margin: 2px 0 0; overflow: hidden; color: #b8beca; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.scam-factors { display: flex; flex-direction: column; gap: 5px; }
.scam-factor-score { color: #e09a87; font-size: 9px; font-variant-numeric: tabular-nums; }
.scam-disclaimer { margin: 8px 0 0; border-left: 2px solid #5a6173; padding-left: 7px; color: #656c79; font-size: 8px; line-height: 1.45; }
.mini-table-wrap { max-height: 230px; overflow: auto; border: 1px solid #242730; border-radius: 7px; }
.mini-table { width: 100%; border-collapse: collapse; font-size: 9px; }
.mini-table th, .mini-table td { border-bottom: 1px solid #22252c; padding: 5px 6px; color: #858c9a; text-align: left; }
.mini-table th { position: sticky; top: 0; background: #17191f; color: #646b79; font-size: 8px; }
.mini-table code { font-size: 8px; }
.risk-checks { display: flex; flex-direction: column; gap: 5px; }
.risk-check { border: 1px solid #242730; border-radius: 7px; background: #17191f; padding: 7px 8px; }
.risk-check > div { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.risk-check strong { font-size: 10px; }
.risk-check small { display: block; margin-top: 3px; color: #6e7583; font-size: 8px; }
.risk-check code { display: block; margin-top: 4px; overflow: hidden; color: #555c69; font-size: 8px; line-height: 1.4; white-space: nowrap; text-overflow: ellipsis; }
.check-status { border-radius: 999px; padding: 2px 6px; font-size: 8px; }
.check-status--verified_safe { color: #7fcda5; } .check-status--verified_risk { color: #ea909a; } .check-status--unknown { color: #d7b373; } .check-status--not_applicable { color: #6c7380; }
.risk-flags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 3px; }
.risk-flags span { border: 1px solid rgba(224, 110, 120, .25); border-radius: 999px; padding: 3px 6px; color: #dc8d95; font-size: 8px; }
.timeline-list { position: relative; display: flex; flex-direction: column; gap: 0; }
.timeline-event { position: relative; display: grid; grid-template-columns: 24px 1fr; gap: 4px; min-height: 54px; }
.timeline-event:not(:last-child)::before { position: absolute; top: 22px; bottom: -2px; left: 9px; width: 1px; background: #2a2e38; content: ''; }
.timeline-marker { z-index: 1; display: grid; width: 19px; height: 19px; place-items: center; border: 1px solid #37405c; border-radius: 50%; background: #1a1d27; color: #9ea8ef; }
.timeline-title { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.timeline-title strong { color: #c4c8d1; font-size: 9px; }
.timeline-title span { color: #5d6471; font-size: 8px; }
.timeline-event p { margin: 4px 0 0; color: #777e8c; font-size: 8px; }
.timeline-event small { display: inline-block; margin-top: 3px; color: #9da6e8; font-size: 8px; }
.timeline-more { margin-top: 6px; }
.inline-empty { display: flex; min-height: 50px; align-items: center; justify-content: center; gap: 6px; color: #656c79; font-size: 9px; text-align: center; }
.detail-footnote { display: flex; align-items: flex-start; gap: 7px; padding: 10px 12px 13px; color: #5f6674; font-size: 8px; line-height: 1.5; }
.radar-detail-placeholder { display: flex; min-height: 260px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 30px; color: #5f6674; text-align: center; }
.radar-detail-placeholder strong { color: #949baa; font-size: 12px; }
.radar-detail-placeholder p { max-width: 280px; margin: 0; font-size: 10px; line-height: 1.5; }
.spinning { animation: radar-spin .9s linear infinite; }
@keyframes radar-spin { to { transform: rotate(360deg); } }

@media (max-width: 1280px) {
  .provider-runtime-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .provider-runtime-strip > div { border-right: 0; }
  .radar-filter-bar { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .filter-actions { justify-content: flex-end; }
  .radar-layout { grid-template-columns: minmax(0, 1.35fr) minmax(360px, .85fr); }
}
@media (max-width: 980px) {
  .radar-workspace { padding: 18px; }
  .radar-hero { flex-direction: column; }
  .radar-health { justify-content: flex-start; }
  .radar-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .radar-filter-bar { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .radar-layout { grid-template-columns: 1fr; }
  .radar-detail-panel { position: static; max-height: none; }
  .holder-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 620px) {
  .radar-workspace { padding: 14px 12px 22px; }
  .provider-runtime-strip { grid-template-columns: 1fr 1fr; }
  .provider-runtime-error { grid-column: 1 / -1; }
  .holder-history-toolbar { align-items: flex-start; flex-direction: column; }
  .holder-history-grid { grid-template-columns: 1fr; }
  .smart-flow-summary, .smart-flow-charts, .crypto-graph-summary { grid-template-columns: 1fr; }
  .graph-path-card { grid-template-columns: 1fr; }
  .graph-arrow { justify-self: center; transform: rotate(90deg); }
  .graph-evidence-card > header, .graph-signal-head { align-items: flex-start; flex-direction: column; }
  .radar-summary-grid, .radar-filter-bar { grid-template-columns: 1fr; }
  .filter-actions { justify-content: stretch; }
  .filter-actions .radar-button { flex: 1; }
  .detail-meta-grid, .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .score-grid, .top-holder-summary, .cluster-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .scam-summary, .scam-social-grid, .outcome-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .flow-grid { grid-template-columns: 1fr; }
}
</style>
